import { format } from 'date-fns';
import { parseDateOnlyString } from '@/utils/dateFormat';
import { getOrganizationWeekday } from '@/utils/productWeekdayAvailability';

/** Fallback timezone when the organization does not expose one (matches the DB default). */
export const DEFAULT_RESERVATION_TIMEZONE = 'America/Costa_Rica';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Calendar day of `date` in the browser's local timezone, formatted `YYYY-MM-DD`. */
function getLocalDayKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Calendar day of `date` as seen from `timezone`, formatted `YYYY-MM-DD`.
 * Uses `en-CA` because it renders ISO-style dates.
 */
export function getReservationDayKey(date: Date, timezone?: string | null): string {
  const timeZone = timezone?.trim() || DEFAULT_RESERVATION_TIMEZONE;
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  } catch {
    // Unknown timezone: fall back to the browser's local calendar day.
    return getLocalDayKey(date);
  }
}

/**
 * Whether reservations are blocked on `date`, combining recurring closed weekdays
 * (0=Sun..6=Sat) OR specific holiday dates. Null/empty inputs mean unrestricted.
 *
 * The day is resolved in two frames and either match blocks: the browser-local day
 * (the frame calendar cells/week slots are drawn in) and the organization-timezone
 * day (the frame clicks and the booking form convert the instant to). Checking only
 * one frame lets selection and highlight disagree when the org timezone differs from
 * the browser timezone.
 */
export function isReservationDayClosed(
  date: Date,
  closedWeekdays?: number[] | null,
  closedDates?: string[] | null,
  timezone?: string | null,
): boolean {
  const weekdays = closedWeekdays ?? [];
  const dates = closedDates ?? [];
  if (weekdays.length === 0 && dates.length === 0) return false;

  const tz = timezone?.trim() || DEFAULT_RESERVATION_TIMEZONE;
  if (weekdays.length > 0) {
    if (weekdays.includes(date.getDay())) return true;
    if (weekdays.includes(getOrganizationWeekday(tz, date))) return true;
  }
  if (dates.length > 0) {
    if (dates.includes(getLocalDayKey(date))) return true;
    if (dates.includes(getReservationDayKey(date, tz))) return true;
  }
  return false;
}

/**
 * True for a real calendar date in `YYYY-MM-DD`. Round-trips through
 * `parseDateOnlyString`, so impossible dates such as `2026-02-31` (which the
 * parser would roll over) are rejected instead of reaching the DB.
 */
export function isValidClosedDate(value: string): boolean {
  if (!ISO_DATE_PATTERN.test(value)) return false;
  const parsed = parseDateOnlyString(value);
  return parsed !== null && format(parsed, 'yyyy-MM-dd') === value;
}
